import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { LuLoaderCircle, LuCircleAlert } from 'react-icons/lu';
import AuthShell from '../components/AuthShell';
import { destinoAposEntrar } from '../config/boasVindas';
import {
  carregarUsuarioLogado,
  conferirState,
  redirectUriGoogle,
  type CadastroGoogle,
} from '../lib/entrarComGoogle';

/**
 * /login/google — a volta do Google no "Continuar com o Google".
 *
 * Três saídas: tem conta → entra (Home, ou Fluxo de Vendas no celular, como o
 * login por senha); não tem → /cadastro com nome e e-mail prontos, faltando só
 * a empresa; deu errado → o motivo e o caminho de volta.
 */
const GoogleLoginCallbackPage = () => {
  const apiUrl = import.meta.env.VITE_API_URL;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [erro, setErro] = useState<string | null>(null);

  /* O StrictMode monta duas vezes em desenvolvimento e o code do Google serve
     UMA vez — sem a trava, a segunda troca falha e a tela mostra erro depois de
     ter dado certo. Mesmo motivo da ConfirmarEmailPage. */
  const jaTentou = useRef(false);

  useEffect(() => {
    if (jaTentou.current) return;
    jaTentou.current = true;

    /* Cancelou na tela do Google: volta para o login, sem alarde. */
    if (searchParams.get('error')) {
      navigate('/login', { replace: true });
      return;
    }

    const code = searchParams.get('code');
    if (!code || !conferirState(searchParams.get('state'))) {
      setErro('Este retorno do Google não veio de um login iniciado aqui. Comece de novo.');
      return;
    }

    (async () => {
      try {
        const resposta = await fetch(`${apiUrl}/api/Auth/Google`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ code, redirectUri: redirectUriGoogle() }),
        });

        const dados = await resposta.json().catch(() => ({}));

        if (!resposta.ok) {
          setErro(dados.message || 'Não foi possível entrar com o Google.');
          return;
        }

        if (dados.status === 'cadastro') {
          const google: CadastroGoogle = { idToken: dados.idToken, nome: dados.nome, email: dados.email };
          navigate('/cadastro', { replace: true, state: { google } });
          return;
        }

        if (!(await carregarUsuarioLogado())) {
          setErro('Entramos, mas não conseguimos carregar a sua conta. Tente de novo.');
          return;
        }

        /* Conta nova pelo Google cai no guia de boas-vindas (28/10/2026); a API é quem diz. */
        navigate(destinoAposEntrar(), { replace: true });
      } catch {
        setErro('Não conseguimos falar com o servidor. Verifique a conexão e tente de novo.');
      }
    })();
  }, [apiUrl, navigate, searchParams]);

  if (erro) {
    return (
      <AuthShell>
        <h1 className="au-h1">Não deu para entrar</h1>
        <div className="au-alert is-danger" role="alert">
          <LuCircleAlert size={16} />
          <span>{erro}</span>
        </div>
        <Link to="/login" replace className="au-btn" style={{ textDecoration: 'none' }}>
          Voltar para o login
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="au-h1">Entrando com o Google</h1>
      <p className="au-sub">
        <LuLoaderCircle size={15} className="au-spin" style={{ verticalAlign: '-2px' }} /> Só um
        instante…
      </p>
    </AuthShell>
  );
};

export default GoogleLoginCallbackPage;
